import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Check, X, Info, AlertTriangle, Loader2 } from 'lucide-react'

type ToastType = 'success' | 'error' | 'info' | 'warning' | 'loading'

interface Toast {
    id: string
    type: ToastType
    title?: string
    message: string
    duration?: number
}

interface ToastContextType {
    showToast: (type: ToastType, message: string, options?: { title?: string; duration?: number }) => void
    removeToast: (id: string) => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

// eslint-disable-next-line react-refresh/only-export-components
export const useToast = () => {
    const context = useContext(ToastContext)
    if (!context) {
        throw new Error('useToast must be used within ToastProvider')
    }
    return context
}

const ToastIcon = ({ type }: { type: ToastType }) => {
    switch (type) {
        case 'success':
            return (
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-400 to-green-600 flex items-center justify-center shadow-[0_0_12px_rgba(16,185,129,0.3)] ring-1 ring-white/20">
                    <Check className="w-4 h-4 text-white stroke-[2.5]" />
                </div>
            )
        case 'error':
            return (
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-red-400 to-rose-600 flex items-center justify-center shadow-[0_0_12px_rgba(244,63,94,0.3)] ring-1 ring-white/20">
                    <X className="w-4 h-4 text-white stroke-[2.5]" />
                </div>
            )
        case 'warning':
            return (
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-orange-600 flex items-center justify-center shadow-[0_0_12px_rgba(245,158,11,0.3)] ring-1 ring-white/20">
                    <AlertTriangle className="w-4 h-4 text-white stroke-[2.5]" />
                </div>
            )
        case 'info':
            return (
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center shadow-[0_0_12px_rgba(99,102,241,0.3)] ring-1 ring-white/20">
                    <Info className="w-4 h-4 text-white stroke-[2.5]" />
                </div>
            )
        case 'loading':
            return (
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-zinc-400 to-zinc-600 dark:from-zinc-700 dark:to-zinc-900 flex items-center justify-center shadow-md ring-1 ring-white/10">
                    <Loader2 className="w-4 h-4 text-white animate-spin" />
                </div>
            )
    }
}

const ToastItem = ({ toast, onClose }: { toast: Toast; onClose: () => void }) => {
    // Determina se deve usar layout compacto (inline)
    const isCompact = !toast.title || toast.title.length < 20

    return (
        <motion.div
            {...({
                layout: true,
                initial: { opacity: 0, y: -20, scale: 0.95 },
                animate: { opacity: 1, y: 0, scale: 1 },
                exit: { opacity: 0, y: -20, scale: 0.95 }
            } as any)}
            transition={{ type: 'spring', damping: 30, stiffness: 400, opacity: { duration: 0.15 } }}
            className={`group relative flex items-center gap-3 w-full md:max-w-sm bg-white/95 dark:bg-black/80 backdrop-blur-xl border border-zinc-200/60 dark:border-white/[0.08] shadow-lg shadow-black/5 dark:shadow-black/20 rounded-xl overflow-hidden ${isCompact ? 'p-2.5 md:p-3' : 'p-3 md:p-3.5'
                }`}
            role="alert"
        >
            {/* Subtle hover glow - apenas no hover */}
            <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none bg-gradient-to-r ${toast.type === 'success' ? 'from-emerald-500/5 to-transparent' :
                toast.type === 'error' ? 'from-rose-500/5 to-transparent' :
                    toast.type === 'warning' ? 'from-amber-500/5 to-transparent' :
                        'from-indigo-500/5 to-transparent'
                }`} />

            {/* Ícone compacto */}
            <div className="flex-shrink-0 relative z-10">
                <ToastIcon type={toast.type} />
            </div>

            {/* Conteúdo - layout adaptativo */}
            <div className="flex-1 min-w-0 relative z-10">
                {isCompact ? (
                    // Layout inline compacto (título e mensagem na mesma linha)
                    <p className="text-sm text-zinc-900 dark:text-white font-medium leading-tight">
                        {toast.title && <span className="font-semibold">{toast.title}</span>}
                        {toast.title && toast.message && <span className="mx-1.5">·</span>}
                        {toast.message && <span className="text-zinc-700 dark:text-zinc-300 font-normal">{toast.message}</span>}
                    </p>
                ) : (
                    // Layout tradicional (2 linhas)
                    <>
                        {toast.title && (
                            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white mb-0.5 leading-tight">
                                {toast.title}
                            </h3>
                        )}
                        <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-snug">
                            {toast.message}
                        </p>
                    </>
                )}
            </div>

            {/* Botão fechar - mais discreto */}
            <button
                onClick={onClose}
                className="flex-shrink-0 p-1 rounded-md text-zinc-400 hover:text-zinc-600 dark:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100/80 dark:hover:bg-white/5 transition-all duration-150 relative z-10"
                aria-label="Fechar notificação"
            >
                <X className="w-3.5 h-3.5" />
            </button>
        </motion.div>
    )
}

export const ToastProvider = ({ children }: { children: ReactNode }) => {
    const [toasts, setToasts] = useState<Toast[]>([])

    const showToast = useCallback((type: ToastType, message: string, options?: { title?: string; duration?: number }) => {
        const id = Math.random().toString(36).substring(7)
        const duration = options?.duration || 5000

        // Auto-generate titles based on type if not provided
        let title = options?.title
        if (!title) {
            switch (type) {
                case 'success': title = 'Sucesso'; break;
                case 'error': title = 'Erro'; break;
                case 'warning': title = 'Atenção'; break;
                case 'info': title = 'Informação'; break;
            }
        }

        const newToast: Toast = {
            id,
            type,
            message,
            title,
            duration
        }

        setToasts(prev => [...prev, newToast])

        if (duration > 0) {
            setTimeout(() => {
                setToasts(prev => prev.filter(t => t.id !== id))
            }, duration)
        }
    }, [])

    const removeToast = useCallback((id: string) => {
        setToasts(prev => prev.filter(t => t.id !== id))
    }, [])

    return (
        <ToastContext.Provider value={{ showToast, removeToast }}>
            {children}

            {/* Toast Container - Mobile: top center, Desktop: bottom right */}
            <div className="fixed top-16 left-1/2 -translate-x-1/2 md:top-auto md:bottom-6 md:right-6 md:left-auto md:translate-x-0 z-[100] flex flex-col gap-3 pointer-events-none px-4 md:px-0 w-full md:w-auto md:max-w-sm">
                <AnimatePresence mode="popLayout">
                    {toasts.map(toast => (
                        <div key={toast.id} className="pointer-events-auto w-full flex justify-center md:justify-end">
                            <ToastItem toast={toast} onClose={() => removeToast(toast.id)} />
                        </div>
                    ))}
                </AnimatePresence>
            </div>
        </ToastContext.Provider>
    )
}
